import { LoanStatus } from '@willow/graphql-iso/src/portal';
import { isBeforeToday } from '@willow/shared-iso';
import { assertNever } from '@willow/shared-web';

import { PortalSelectedLoan } from '../../App';
import { isTransferredView } from '../../utils/loanView';

type LoanStatusView = 'preActive' | 'paidOff' | 'transferred' | 'inactive';
type PageViewType = LoanStatusView | 'maturing' | 'maturedWithPausedPayments' | 'pausedPayments' | 'payment';

export const getPageViewType = (loan: PortalSelectedLoan): PageViewType => {
  const {
    company: { paymentsPaused: companyPaymentsPaused, portalSettings },
    currentSnapshot: {
      loanStatus,
      stopSettings: { portalPayments },
      maturityConfig: { isMaturing, maturityDate },
      totalAmountRemainingFromBorrower,
      isFullInterestOnlyLoan,
    },
  } = loan;

  // Loan status-specific views
  switch (loanStatus) {
    case LoanStatus.Active:
    case LoanStatus.Foreclosure:
      break;
    case LoanStatus.Archived:
    case LoanStatus.Reo:
    case LoanStatus.Liquidated:
      return 'inactive';
    case LoanStatus.PaidOff:
      return 'paidOff';
    case LoanStatus.PendingActivation:
    case LoanStatus.PreActive:
      return 'preActive';
    case LoanStatus.PendingTransfer:
    case LoanStatus.Transferred:
      if (isTransferredView(loan)) {
        return 'transferred';
      }
      break;
    default:
      assertNever(loanStatus);
  }

  // Paused payments
  if (companyPaymentsPaused || portalPayments === 'none') {
    return 'pausedPayments';
  }

  if (
    isBeforeToday(new Date(maturityDate), true) &&
    totalAmountRemainingFromBorrower > 0 &&
    !portalSettings.borrowerPayments.allowPaymentsPastMaturity
  ) {
    return 'maturedWithPausedPayments';
  }

  // Maturing loan
  // NB customer and borrower expecations differ around maturing loans so we
  // treat them as if they're in the normal payment status even when they're
  // technically maturing
  if (isMaturing && totalAmountRemainingFromBorrower === 0 && !isFullInterestOnlyLoan) {
    return 'maturing';
  }

  return 'payment';
};
